/**
 * Écran de confirmation de refus pour les référents
 * Phase 7 - Écran Acceptation Référent (UX)
 * Dupliqué et adapté de RefusalConfirmationState.tsx
 */

import { XCircle } from 'lucide-react';
import { BrandingSettings } from '@/hooks/useReferentBranding';

interface ReferentRefusalConfirmationScreenProps {
  candidateName: string;
  refusalReason?: string;
  branding: BrandingSettings | null;
}

export const ReferentRefusalConfirmationScreen = ({
  refusalReason,
  branding
}: ReferentRefusalConfirmationScreenProps) => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 font-['Bricolage_Grotesque']">
      <div className="w-full max-w-[646px] bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
        {/* Header avec logo et badge */}
        <div className="self-stretch px-4 sm:px-7 py-6 border-b border-neutral-400/20 flex flex-col sm:flex-row justify-between items-center gap-4">
          <img 
            className="w-32 sm:w-40 h-auto" 
            src={(branding?.enabled && branding?.logoUrl) ? branding.logoUrl : "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"}
            alt={(branding?.enabled && branding?.logoUrl) ? "Logo Entreprise" : "Getproof Logo"}
          />
          <div 
            className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
            style={{
              outlineColor: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf',
              color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf'
            }}
          >
            <div className="justify-start text-xs sm:text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight text-center">
              Demande de référence professionnelle
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="self-stretch px-4 sm:px-7 py-8 flex flex-col justify-center items-center gap-6 text-center">
          {/* Icône et titre */}
          <div className="flex flex-col items-center gap-4">
            <XCircle className="h-16 w-16 text-red-500" />
            <div className="flex flex-col gap-2">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 font-['Bricolage_Grotesque']">
                Refus enregistré
              </h2>
              <p className="text-sm text-neutral-400 font-normal font-['Bricolage_Grotesque']">
                Votre refus de fournir une référence a été enregistré et le recruteur en a été informé.
              </p>
            </div>
          </div>

          {/* Motif du refus */}
          {refusalReason && (
            <div className="w-full bg-gray-50 p-4 rounded-lg text-left">
              <div className="text-sm font-medium text-gray-800 font-['Bricolage_Grotesque'] mb-2">
                Motif du refus :
              </div>
              <div className="text-sm text-gray-700 font-normal font-['Bricolage_Grotesque']">
                {refusalReason}
              </div>
            </div>
          )}

          {/* Message final */}
          <div className="w-full flex flex-col gap-4">
            <p className="text-sm text-neutral-400 font-normal font-['Bricolage_Grotesque']">
              Cette décision est définitive. Le recruteur a été informé de votre refus.
            </p>
          </div>

          {/* Séparateur */}
          <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

          {/* Footer */}
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="w-full max-w-96 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-none">
              Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
            </div>
            <div 
              className="text-center justify-center text-sm font-normal font-['Bricolage_Grotesque'] leading-tight"
              style={{ color: (branding?.enabled && branding?.primaryColor) ? branding.primaryColor : '#2dd4bf' }}
            >
              L'équipe Getproof
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};